import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrderDetails } from '../../redux/slices/orderSlice';


const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get data from navigation state
  const { orderId, message, order: locationOrder } = location.state || {};
  
  // Get order from orders slice (when fetched by ID)
  const { orderDetails, loading: orderLoading, error: orderError } = useSelector((state) => state.orders);
  
  // Get order from checkout slice (when coming from checkout finalization)
  const { order: checkoutOrder } = useSelector((state) => state.checkout);
  
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    // If we have orderId and haven't fetched yet → fetch the order details
    if (orderId && !isFetching && !orderDetails) {
      console.log('🔄 Fetching order details for:', orderId);
      setIsFetching(true);
      dispatch(fetchOrderDetails(orderId));
    }
  }, [orderId, isFetching, orderDetails, dispatch]);

  // Determine which order to display
  // Priority: orderDetails (fetched) > checkoutOrder (from finalization) > locationOrder (passed)
  const order = orderDetails || checkoutOrder || locationOrder;

  console.log('📦 Final order to display:', order);

  // Show loading while fetching
  if (orderId && orderLoading && !order) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading order details...</span>
      </div>
    );
  }

  // If we tried to fetch but got an error and have no fallback data
  if (orderId && orderError && !order) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <p className="text-lg text-red-600">Failed to load order details.</p>
        <p className="text-gray-600">{orderError}</p>
        <button 
          onClick={() => navigate('/my-orders')} 
          className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
        >
          Go to My Orders
        </button>
      </div>
    );
  }

  // ✅ FIXED: Only redirect if there's truly no order data AND no orderId being fetched
  // This prevents redirect when order data is being passed from checkout
  if (!order && !orderId && !orderLoading) {
    console.log('⚠️ No order data found, redirecting...');
    setTimeout(() => {
      navigate('/my-orders', { replace: true });
    }, 1500);
    
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <p className="text-lg text-gray-600 mb-4">No order data found. Redirecting...</p>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // ✅ ADDED: Show loading if we're waiting for data
  if (!order) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading order details...</span>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimatedDelivery = () => {
    const date = new Date(order.createdAt);
    date.setDate(date.getDate() + 7);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPaymentStatusDisplay = () => {
    if (order.paymentStatus) {
      return order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1);
    }
    return order.isPaid ? 'Paid' : 'Unpaid';
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      {/* Success Banner */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-md p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          <div className="text-center sm:text-left flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h1>
            <p className="text-black text-lg mb-2">
              {message || 'Thank you for your purchase. Your order has been confirmed and is being processed.'}
            </p>
            <p className="text-gray-600">
              A confirmation email has been sent to <span className="font-medium text-black">{order.user?.email}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Order Container */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Order Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900">Order #{order._id}</h3>
              <p className="text-gray-600 mt-1">Placed on {formatDate(order.createdAt)}</p>
            </div>

            <div className="flex flex-col items-start sm:items-end mt-4 sm:mt-0 space-y-2">
              <span className="px-3 py-1 rounded-lg border border-yellow-800 text-sm font-medium bg-yellow-100 text-yellow-800">
                Order Status: {order.status || 'Processing'}
              </span>
              <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                order.isPaid ? 'bg-green-100 border border-green-800 text-green-800' : 'bg-red-100 border border-red-800 text-red-800'
              }`}>
                Payment Status: {getPaymentStatusDisplay()}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Delivery ETA */}
          <div className="bg-gray-50 border-l-4 border-gray-500 p-4 mb-6 rounded-lg">
            <p className="font-medium text-black">Estimated Delivery</p>
            <p className="text-gray-900 font-semibold">{estimatedDelivery()}</p>
          </div>

          {/* Order Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Order Summary</h4>
              <p><strong>Total:</strong> ₹{order.totalPrice?.toFixed(2)}</p>
              <p><strong>Payment Method:</strong> {order.paymentMethod}</p>
              <p><strong>Items:</strong> {order.orderItems?.length}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Customer</h4>
              <p><strong>Name:</strong> {order.user?.name || 'N/A'}</p>
              <p><strong>Email:</strong> {order.user?.email}</p>
              <p><strong>Phone:</strong> {order.shippingAddress?.mobile || order.shippingAddress?.phone}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Shipping Address</h4>
              <p>{order.shippingAddress?.address}</p>
              <p>{order.shippingAddress?.city}, {order.shippingAddress?.state}</p>
              <p>{order.shippingAddress?.postalCode}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-4">Order Items</h4>

            {order.orderItems?.map((item, idx) => (
              <div key={idx} className="flex items-center border-b pb-3 mb-3">
                <img src={item.image} alt="" className="w-20 h-20 rounded-md object-cover mr-4" />

                <div className="flex-1">
                  <p className="font-medium text-gray-900">{item.name}</p>
                  <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                </div>

                <p className="font-semibold text-gray-900">
                  ₹{(item.price * item.quantity).toFixed(2)}
                </p>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-6">
            <button onClick={() => navigate('/my-orders')} className="flex-1 bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition">
              Track Orders
            </button>
            <button onClick={() => navigate('/')} className="flex-1 bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition">
              Continue Shopping
            </button>
          </div>



                    {/* Help Section */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-black mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Need Help?</h4>
                <p className="text-sm text-gray-600 mb-2">
                  If you have any questions about your order, our support team is here to help.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <Link to="/contact" className="text-black underline font-semibold">
                    Contact Support
                  </Link>
                  <button
                    onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                    className="text-black underline font-semibold"
                  >
                    Additional links
                  </button>
                </div>
              </div>
            </div>
          </div>


          
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
